import type { GetMarketTradersRow } from '@morfi/backend-sdk-v2/client';
import {
  exactTraderPnlD6,
  type MarketPoolQuoteSnapshot,
  type PoolMathUnavailableReason,
} from '@morfi/market-math';
import type { UserIdentityShape } from '@/components/profile/user-identity';

export { exactTraderPnlD6 } from '@morfi/market-math';

export type MarketTradersSort = 'volume' | 'realized' | 'unrealized' | 'total';

export interface MarketTraderViewModel {
  participationId: string;
  trader: UserIdentityShape;
  realizedPnlD6: string;
  /** Exact gross full-exit value plus unclaimed prize, less remaining basis. */
  unrealizedPnlD6: string | null;
  /** Realized plus unrealized PnL, or null when the current value is unavailable. */
  totalPnlD6: string | null;
  valuationUnavailableReason: PoolMathUnavailableReason | null;
  totalVolumeD6: string;
  tradeCount: number;
  rank: number;
}

export function toMarketTraderViewModel(
  row: GetMarketTradersRow,
  snapshot: MarketPoolQuoteSnapshot | undefined,
): MarketTraderViewModel {
  const valuation =
    snapshot === undefined
      ? ({ available: false, reason: 'missing_bonding_curve_state' } as const)
      : exactTraderPnlD6(row, snapshot);
  return {
    participationId: row.participationId,
    trader: traderIdentity(row),
    realizedPnlD6: row.realizedPnlD6,
    unrealizedPnlD6: valuation.available ? valuation.value.unrealizedPnlD6.toString() : null,
    totalPnlD6: valuation.available ? valuation.value.totalPnlD6.toString() : null,
    valuationUnavailableReason: valuation.available ? null : valuation.reason,
    totalVolumeD6: row.volumeQuoteD6,
    tradeCount: row.tradeCount,
    rank: row.rank,
  };
}

function traderIdentity(row: GetMarketTradersRow): UserIdentityShape {
  if (row.profile !== null) {
    return {
      ...row.profile,
      walletAddress: row.walletAddress,
      connectedProviders: row.profile.connectedProviders ?? [],
    };
  }
  return {
    userId: null,
    walletAddress: row.walletAddress,
    displayName: row.walletAddress,
    handle: null,
    avatarUrl: null,
    connectedProviders: [],
  };
}
